<template>
  <section class="tw:flex tw:max-w-[760px] tw:flex-col tw:gap-7" data-testid="reg-step-goal">
    <RegistrationStepHeading :title="$t('registration.goal.title')" :lede="$t('registration.goal.lede')" />

    <div class="tw:flex tw:flex-col tw:gap-1.5">
      <span class="tw:text-13 tw:font-medium tw:text-gray-700">{{ $t('registration.goal.main_goal') }}</span>
      <div class="tw:flex tw:flex-row tw:gap-2.5" role="radiogroup" :aria-label="$t('registration.goal.main_goal')">
        <LeadRadioCard
          v-for="option in mainGoalOptions"
          :key="option.value"
          v-model="mainGoal"
          name="reg-goal-main"
          :value="option.value"
          :icon="option.icon"
          :title="option.label"
          :description="option.description"
          :data-testid="`reg-goal-main-${option.value}`" />
      </div>
    </div>

    <div class="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2">
      <LeadSelect
        v-model="rolesPerYear"
        :options="rolesPerYearOptions"
        :label="$t('registration.goal.roles_per_year')"
        :placeholder="$t('registration.goal.roles_per_year_placeholder')"
        data-testid="reg-goal-roles-per-year" />

      <LeadSelect
        v-model="whenToStart"
        :options="startWindowOptions"
        :label="$t('registration.goal.start_window')"
        :placeholder="$t('registration.goal.start_window_placeholder')"
        data-testid="reg-goal-start-window" />
    </div>
  </section>
</template>

<script setup lang="ts">
/** Step 5 - the only skippable step, via the store's generic `SKIPPABLE_STEPS`; Continue stays blocked until all three fields are chosen */
import { computed, onMounted, watch, type Component } from 'vue';
import { IconAd, IconTarget, IconUsers } from '@tabler/icons-vue';

import { registrationClient } from '@/api/registration';
import LeadRadioCard from '@/components/form/LeadRadioCard.vue';
import LeadSelect, { type LeadSelectOption } from '@/components/lead-design/LeadSelect.vue';
import RegistrationStepHeading from '@/components/registration/subcomponents/RegistrationStepHeading.vue';
import { useRegistrationStepSync } from '@/composables/useRegistrationStepSync';
import translator from '@/locale/translator';
import { useRegistrationStore } from '@/stores/registration';
import type { HiringGoal, RolesPerYear, StartWindow } from '@/types/registration';

const MAIN_GOAL_ICONS: Record<HiringGoal, Component> = { hard: IconTarget, volume: IconUsers, brand: IconAd };
const MAIN_GOALS: HiringGoal[] = ['hard', 'volume', 'brand'];
const ROLES_PER_YEAR: RolesPerYear[] = ['3', '12', '40', '100'];
const START_WINDOWS: StartWindow[] = ['now', 'week', 'month', 'later'];

/* ----------------------------------- STATE ---------------------------------- */
const allowNext = defineModel<boolean>('allowNext', { required: true });

const store = useRegistrationStore();

/* --------------------------------- COMPUTED --------------------------------- */
// bound to the draft so a reload recovers them
const mainGoal = computed({
  get: () => store.draft.goal.mainGoal,
  set: (value: HiringGoal | '') => {
    store.draft.goal.mainGoal = value;
  },
});

// LeadSelect's v-model is `string | string[] | null`; the setter narrows back to this field's union
const rolesPerYear = computed({
  get: () => store.draft.goal.rolesPerYear,
  set: (value: string | string[] | null) => {
    store.draft.goal.rolesPerYear = (value ?? '') as RolesPerYear | '';
  },
});

const whenToStart = computed({
  get: () => store.draft.goal.whenToStart,
  set: (value: string | string[] | null) => {
    store.draft.goal.whenToStart = (value ?? '') as StartWindow | '';
  },
});

const mainGoalOptions = computed(() =>
  MAIN_GOALS.map((value) => ({
    value,
    icon: MAIN_GOAL_ICONS[value],
    label: translator.get(`registration.goal.main_goal.${value}`),
    description: translator.get(`registration.goal.main_goal.${value}_description`),
  }))
);

const rolesPerYearOptions = computed<LeadSelectOption[]>(() =>
  ROLES_PER_YEAR.map((value) => ({ value, label: translator.get(`registration.goal.roles_per_year.${value}`) }))
);

const startWindowOptions = computed<LeadSelectOption[]>(() =>
  START_WINDOWS.map((value) => ({ value, label: translator.get(`registration.goal.start_window.${value}`) }))
);

// every field is required to submit; otherwise the skip link records the skip instead
const isFormValid = computed(() => mainGoal.value !== '' && rolesPerYear.value !== '' && whenToStart.value !== '');

/* --------------------------------- METHODS ---------------------------------- */
async function submit(): Promise<boolean> {
  await registrationClient.submitGoal({
    mainGoal: mainGoal.value,
    rolesPerYear: rolesPerYear.value,
    whenToStart: whenToStart.value,
  });

  return true;
}

async function skip() {
  await registrationClient.skipGoal();
}

// pre-fills the draft from the backend on every entry; `isFormValid` already watches the draft
useRegistrationStepSync('goal');

/* -------------------------------- LIFECYCLE --------------------------------- */
onMounted(() => {
  store.availableSteps.goal.onContinue = submit;
  store.availableSteps.goal.onSkip = skip;
  allowNext.value = isFormValid.value;
});

/* --------------------------------- WATCHERS --------------------------------- */
watch(isFormValid, (valid) => {
  allowNext.value = valid;
});
</script>
